<template>
    <section class="comboParlay">
        <header class="comboParlay-header">
            <h2 class="comboParlay-title">
                {{ i18n.t('betSlip_comboParlay') }}
            </h2>
            <button
                v-if="!isValid2MpSubBets"
                class="comboParlay-infoButton"
                v-bind:class="`${gaClassPrefix}_Combo_Btn_Info`"
                type="button"
                aria-label="How to play Combo Parlay"
                v-on:click="openComboInfo"
            >
                <SboIcon name="system:infoCircleOutline" size="20" />
            </button>
        </header>
        <div class="comboParlay-options">
            <ComboParlayOptionCard
                v-for="option in options"
                v-bind:key="option.fold"
                v-bind:name="getOptionName(option)"
                v-bind:odds="getOptionOdds(option)"
                v-bind:payout="formatMoneyWithDecimal(option.payout)"
                v-bind:canCashOut="option.canCashOut"
                v-bind:disabled="disabled"
                v-bind:modelValue="option.isSelected"
                v-on:update:modelValue="emit('toggle', option.fold)"
            />
        </div>
        <ComboParlayInfoPopup v-if="isShowComboInfo" v-on:close="closeComboInfo" />
    </section>
</template>

<script setup lang="ts">
    import { ref } from 'vue';
    import ComboParlayInfoPopup from '@/components/betSlip/ComboParlayInfoPopup.vue';
    import ComboParlayOptionCard from '@/components/betSlip/Single/ComboParlayOptionCard.vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import type { IComboParlayOption } from '@/libraries/composables/useParlayInSingle';
    import { i18n } from '@/libraries/i18n';
    import { toFixedDecimalPoint } from '@/libraries/utils/text';
    import { useAccountStore } from '@/stores/accountStore';

    const SINGLE_NAMED_FOLD_MAX = 3;
    const COMBO_ODDS_DECIMAL_PLACES = 3;

    withDefaults(defineProps<{
        options: IComboParlayOption[];
        gaClassPrefix?: string;
        isValid2MpSubBets?: boolean;
        disabled: boolean;
    }>(), {
        gaClassPrefix: '',
        isValid2MpSubBets: false,
    });

    const emit = defineEmits<{
        toggle: [fold: number];
    }>();

    const { formatMoneyWithDecimal } = useAccountStore();

    const isShowComboInfo = ref(false);

    function getFoldName(fold: number): string {
        return fold <= SINGLE_NAMED_FOLD_MAX
            ? i18n.t(`betSlip_comboParlay_option_${fold}`)
            : i18n.t('betSlip_comboParlay_option_n', { n: fold });
    }

    function getOptionName(option: IComboParlayOption): string {
        if (option.betCount > 1) {
            return `${getFoldName(option.fold)} (${i18n.t('betSlip_n_bets', { n: option.betCount })})`;
        }
        return getFoldName(option.fold);
    }

    function getOptionOdds(option: IComboParlayOption): string | undefined {
        return option.betCount === 1
            ? toFixedDecimalPoint(option.odds, COMBO_ODDS_DECIMAL_PLACES)
            : undefined;
    }

    function openComboInfo() {
        isShowComboInfo.value = true;
    }

    function closeComboInfo() {
        isShowComboInfo.value = false;
    }
</script>

<style lang="scss" scoped>
@import "@/scss/components/singleMode/comboParlay";
</style>
